import { useDispatch, useSelector } from 'react-redux';
import AudioLoading from './components/AudioLoading';
import { RootState } from '@/store/storeTTS';
import RtcClient from '@/libTTS/RtcClient';
import { setInterruptMsg } from '@/store/storeTTS/slices/room';
import { useDeviceState } from '@/libTTS/useCommon';
import { COMMAND } from '@/utils/utilsTTS/handler';
import style from './index.module.less';
import StopRobotBtn from '@/assets/img/StopRobotBtn.svg';
import { useEffect, useState, useRef } from 'react';

const THRESHOLD_VOLUME = 5;

function AudioController(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  const dispatch = useDispatch();
  const room = useSelector((state: RootState) => state.room);
  const volume = room.localUser.audioPropertiesInfo?.linearVolume || 0;
  const { isAudioPublished } = useDeviceState();
  const isAITalking = room.isAITalking;
  const isAIThinking = room.isAIThinking;
  const isLoading = volume >= THRESHOLD_VOLUME && isAudioPublished;

  // 添加状态文本
  const [statusText, setStatusText] = useState('请开始说话...');
  const lastActivityRef = useRef(Date.now());
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [micPermissionError, setMicPermissionError] = useState(false);
  const permissionCheckInterval = useRef<NodeJS.Timeout | null>(null);
  // 清除超时定时器的函数
  const clearStatusTimeout = () => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    // 监听 AI 状态变化
    const handleAIStatusChange = (data: { status: string }) => {
      const { status } = data;
      lastActivityRef.current = Date.now();

      // 清除之前的定时器
      clearStatusTimeout();

      switch (status) {
        case 'listening':
          // 只有当用户没有在说话时才设置状态为"正在听..."
          if (!(volume >= THRESHOLD_VOLUME && isAudioPublished)) {
            setStatusText('正在听...');
          }
          // 如果长时间没有活动，显示"请开始说话..."
          statusTimeoutRef.current = setTimeout(() => {
            if (Date.now() - lastActivityRef.current >= 5000) {
              setStatusText('请开始说话...');
            }
          }, 5000);
          break;
        case 'thinking':
        case 'speaking':
          setStatusText('正在回答...');
          break;
        case 'finished':
        case 'interrupted':
          // 保持当前状态文本几秒钟后重置为"请开始说话..."
          setStatusText('请开始说话...');
          break;
      }
    };

    // 添加事件监听器
    const eventEmitter = (window as any).ttsEventEmitter;
    if (eventEmitter) {
      eventEmitter.on('aiStatusChange', handleAIStatusChange);
    }

    // 清理事件监听器
    return () => {
      if (eventEmitter) {
        eventEmitter.off('aiStatusChange', handleAIStatusChange);
      }
      clearStatusTimeout();
    };
  }, [volume, isAudioPublished]);

  // 监听用户说话状态变化 - 用户说话优先级最高
  useEffect(() => {
    // 当检测到音量超过阈值且麦克风开启时，立即显示"正在听..."
    if (volume >= THRESHOLD_VOLUME && isAudioPublished) {
      if (statusText !== '正在听...') {
        setStatusText('正在听...');
        clearStatusTimeout();
      }
    }
    // 当没有检测到音量或麦克风关闭时的处理逻辑
    else if (statusText === '正在听...' && !isAITalking && !isAIThinking) {
      setStatusText('请开始说话...');
    }
  }, [volume, isAudioPublished, isAITalking, isAIThinking, statusText]);

  // 监听AI状态变化 - 仅当用户没有说话时更新状态
  useEffect(() => {
    // 只有当用户没有在说话时，才根据AI状态更新文本
    if (!(volume >= THRESHOLD_VOLUME && isAudioPublished)) {
      if ((isAITalking || isAIThinking) && statusText !== '正在回答...') {
        setStatusText('正在回答...');
        clearStatusTimeout();
      } else if (!(isAITalking || isAIThinking) && statusText === '正在回答...') {
        setStatusText('请开始说话...');
      }
    }
  }, [isAITalking, isAIThinking, volume, isAudioPublished, statusText]);

  // 检查麦克风权限状态
  const checkMicPermission = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicPermissionError(true);
        return;
      }

      // 尝试获取媒体流以检查权限
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // 如果成功获取到流，关闭它并设置权限状态为正常
      stream.getTracks().forEach((track) => track.stop());
      setMicPermissionError(false);

    } catch (err) {
      // 如果被拒绝或阻止，设置错误状态
      setMicPermissionError(true);
      console.log('麦克风权限被拒绝或阻止:', err);
    }
  };
  useEffect(() => {
    // 初始检查
    checkMicPermission();

    // 设置定时检查权限状态 (每5秒检查一次)
    permissionCheckInterval.current = setInterval(checkMicPermission, 5000);

    // 监听页面可见性变化，当用户返回页面时重新检查权限
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkMicPermission();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 清理函数
    return () => {
      if (permissionCheckInterval.current) {
        clearInterval(permissionCheckInterval.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const isMicEnabled = isAudioPublished && !micPermissionError;
  //isLoading && 
  return (
    <div className={`${className}`} {...rest}>
      {isMicEnabled ? (
        <div className={style.controllerContainer}>
          {isLoading && <AudioLoading loading={isLoading} color={isAudioPublished ? undefined : '#EAEDF1'} />}
          <div className={style.statusText}>{statusText}</div>
        </div>
      ) : (
        <div className={style.closed}>您已静音</div>
      )}
    </div>
  );
}

export default AudioController;